import type { IExperimentUsage } from '../../modal-pick-template/types';
import type { DisableableAction } from '@shopify/polaris';

import {
  CampaignPickItemHoverActions,
  CampaignPickItemMobileActions,
  CampaignPickItemRow,
} from '~/modules/campaign/components/CampaignPickItem';
import { BlockStack, Box, Icon, InlineGrid, ResourceItem, Text } from '@shopify/polaris';
import { getCampaignStatusInfo } from '~/modules/campaign/helpers';
import { useCampaignNavigate } from '~/modules/campaign/hooks';
import { ICampaignType } from '~/modules/campaign/types';
import { CAMPAIGN_STATUS } from '~/constants/campaign';
import { BulletIcon } from '@shopify/polaris-icons';
import { useTranslation } from 'react-i18next';

interface IProps {
  usage: IExperimentUsage;
  onOpenRemoveConfirm: (usage: IExperimentUsage) => void;
}

export const EditExperimentItem: React.FC<IProps> = ({ usage, onOpenRemoveConfirm }) => {
  const { t } = useTranslation();
  const { onEditCampaign } = useCampaignNavigate();

  const { campaignId, campaignName, campaignStatus, campaignType, resourceName, productStatus } =
    usage;
  const { name: statusName, customColor } = getCampaignStatusInfo(campaignStatus);

  // A URL test names the page it sends traffic to. For a price test, Control is normally the
  // published (Active) product and the duplicate created for the test is Unlisted — naming which one
  // this row holds is what tells the two apart in the list. Only claim a status we actually matched;
  // DRAFT/ARCHIVED/not-yet-loaded fall back to the neutral label.
  const getResourceLabel = () => {
    if (campaignType === ICampaignType.URL) return t('Page: {{name}}', { name: resourceName });
    if (productStatus === 'UNLISTED')
      return t('Unlisted product: {{name}}', { name: resourceName });
    if (productStatus === 'ACTIVE') return t('Active product: {{name}}', { name: resourceName });
    return t('Product: {{name}}', { name: resourceName });
  };

  const actions: DisableableAction[] = [];

  // Only a draft experiment can hand its product back — the same rule the template card follows.
  if (campaignStatus === CAMPAIGN_STATUS.DRAFT) {
    actions.push({
      content: t('Remove from experiment'),
      onAction: () => onOpenRemoveConfirm(usage),
    });
  }

  actions.push({
    content: t('Edit'),
    onAction: () => onEditCampaign({ type: campaignType, id: campaignId }, { newTab: true }),
  });

  return (
    <CampaignPickItemRow>
      <ResourceItem
        id={campaignId}
        verticalAlignment="center"
        name={campaignName}
        onClick={() => {}}
      >
        <InlineGrid columns="minmax(0, 1fr) auto" gap="200" alignItems="center">
          <BlockStack gap="100">
            <div className="min-w-0">
              <Text as="h3" variant="bodyLg" truncate fontWeight="semibold">
                {campaignName}
              </Text>
            </div>

            <div className="flex min-w-0 items-center gap-1">
              {!!resourceName && (
                <div className="min-w-0">
                  <Text as="span" variant="bodyMd" tone="subdued" truncate>
                    {getResourceLabel()}
                  </Text>
                </div>
              )}

              <div className="flex shrink-0 items-center gap-1">
                {!!resourceName && (
                  <Box padding="0">
                    <Icon tone="subdued" source={BulletIcon} />
                  </Box>
                )}
                <span className="font-[550]" style={{ color: customColor }}>
                  {t(statusName)}
                </span>
                <Text as="span" variant="bodyMd" tone="subdued">
                  {t('experiment')}
                </Text>
              </div>
            </div>
          </BlockStack>

          <CampaignPickItemHoverActions actions={actions} />
        </InlineGrid>
      </ResourceItem>

      <CampaignPickItemMobileActions actions={actions} />
    </CampaignPickItemRow>
  );
};
